import { useEffect, useState } from 'react'
import { api } from '../lib/api'

/**
 * Notification priming (v0.20.0, D-119).
 *
 * Measured problem: 0 of 9 members had a push subscription, which meant every
 * reminder the app can send — birthdays, events, on-this-day — reached nobody.
 * The whole notification subsystem was running into a void.
 *
 * Deliberately a SOFT prompt first: we never call the browser permission API cold.
 * A denied browser permission is effectively permanent and unrecoverable in-app, so
 * asking in our own UI first — and only calling the real API after a yes — protects
 * the one chance we get. Dismissal is remembered for 30 days, not forever, because
 * "not now" during a busy week is not "never".
 */
export default function PushPrompt() {
  const [state, setState] = useState<'checking' | 'hidden' | 'ask' | 'busy' | 'done' | 'blocked'>('checking')

  useEffect(() => {
    const snoozed = Number(localStorage.getItem('fa_push_snoozed_at') || 0)
    if (snoozed && Date.now() - snoozed < 30 * 864e5) { setState('hidden'); return }
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) { setState('hidden'); return }
    if (Notification.permission === 'denied') { setState('hidden'); return }
    api.getPushStatus()
      .then(async s => {
        if (!s.key) return setState('hidden')          // server has no VAPID key configured
        if (s.subscribed) return setState('hidden')
        const reg = await navigator.serviceWorker.getRegistration()
        const sub = await reg?.pushManager.getSubscription()
        setState(sub ? 'hidden' : 'ask')
      })
      .catch(() => setState('hidden'))
  }, [])

  const enable = async () => {
    setState('busy')
    try {
      const reg = await navigator.serviceWorker.getRegistration()
      const { key } = await api.getPushStatus()
      if (!reg || !key) return setState('hidden')
      const raw = Uint8Array.from(atob(key.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0))
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: raw })
      const j = sub.toJSON()
      await api.subscribePush({ endpoint: sub.endpoint, keys: { p256dh: j.keys?.p256dh || '', auth: j.keys?.auth || '' } })
      setState('done')
    } catch {
      setState(Notification.permission === 'denied' ? 'blocked' : 'hidden')
    }
  }

  const snooze = () => { localStorage.setItem('fa_push_snoozed_at', String(Date.now())); setState('hidden') }

  if (state === 'checking' || state === 'hidden') return null
  if (state === 'done') return (
    <div className="card-glass p-3 mb-4 border-emerald-500/25">
      <p className="text-sm text-emerald-300">🔔 You'll hear about birthdays and family gatherings.</p>
    </div>
  )
  if (state === 'blocked') return (
    <div className="card-glass p-3 mb-4 border-amber-500/25">
      <p className="text-xs text-amber-300">Notifications are blocked for this site in your browser settings — you can re-allow them there.</p>
    </div>
  )

  return (
    <div className="card-glass p-4 mb-4 border-gold-500/25 fade-up">
      <h3 className="font-display text-base font-semibold text-slate-100 mb-1">Never miss a birthday</h3>
      <p className="text-xs text-slate-500 mb-3">
        We'll only ever nudge you about the family — a birthday that morning, a gathering someone plans,
        a memory from this day. Nothing else, ever.
      </p>
      <div className="flex gap-2">
        <button onClick={enable} disabled={state === 'busy'} className="btn-gold !py-2 text-sm">
          {state === 'busy' ? 'Turning on…' : '🔔 Remind me'}
        </button>
        <button onClick={snooze} className="btn-ghost !py-2 text-sm">Not now</button>
      </div>
    </div>
  )
}
